import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { MapShell } from "@/components/MapShell";
import { makeBaseLayer, loadMapConfig } from "@/lib/mapBase";
import { promptDialog } from "@/components/Confirm";

const pin = (color = "#dc2626") => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
  iconAnchor: [9, 16],
});

const drop = () => L.divIcon({ className: "", html: `<div style="width:15px;height:15px;border-radius:50% 50% 50% 0;background:#0ea5e9;border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`, iconAnchor: [7, 13] });

const numIcon = (n, color) => L.divIcon({ className: "", html: `<div style="width:20px;height:20px;border-radius:50%;background:${color};color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${n}</div>`, iconAnchor: [10, 10] });

const ROUTE_COLORS = ["#2563eb", "#f59e0b"];
export const PALETTE = [
  { c: "#2563eb", label: "Blauw" },
  { c: "#dc2626", label: "Rood · gevaar" },
  { c: "#16a34a", label: "Groen · opstelplaats" },
  { c: "#f59e0b", label: "Oranje" },
  { c: "#7c3aed", label: "Paars" },
  { c: "#0d9488", label: "Teal" },
  { c: "#0f172a", label: "Zwart" },
];

// Read-only mini map showing a single incident location marker (+ optional water points). Has a satellite toggle.
export function IncidentMiniMap({ lat, lng, label, water = [], routes = [], tweezijdig = false, vehiclePos = null, drawings = null, assignedRoute = null, sharedLayer = null, legend = null }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const baseRef = useRef(null);
  const routesRef = useRef(null);
  const drawRef = useRef(null);
  const sharedRef = useRef(null);
  const [showShared, setShowShared] = useState(true);
  const [sat, setSat] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!el.current || mapRef.current || lat == null || lng == null) return;
    const map = L.map(el.current, { zoomControl: true, attributionControl: false }).setView([lat, lng], 16);
    mapRef.current = map;
    loadMapConfig().then((c) => setCfg(c || {}));
    L.marker([lat, lng], { icon: pin() }).addTo(map).bindPopup(label || "");
    water.forEach((w) => { L.marker([w.lat, w.lng], { icon: drop() }).addTo(map).bindPopup(`💧 ${w.name}${w.note ? " · " + w.note : ""}`); });
    setTimeout(() => map.invalidateSize(), 200);
  }, [lat, lng, label, water]);

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(cfg, sat).addTo(map);
    baseRef.current.bringToBack();
  }, [cfg, sat]);

  // Draw the two approach routes ("tweezijdig aanrijden") — manual if defined, else indicative auto-lines.
  useEffect(() => {
    const map = mapRef.current; if (!map || lat == null || lng == null) return;
    if (routesRef.current) map.removeLayer(routesRef.current);
    const grp = L.layerGroup().addTo(map); routesRef.current = grp;
    const pts = [];
    const manual = (routes || []).filter((r) => r && r.length >= 1);
    if (manual.length) {
      let hl = (assignedRoute != null && manual[assignedRoute]) ? assignedRoute : null;
      if (hl == null && cfg && cfg.highlight_nearest_route && vehiclePos && manual.length > 1) {
        let bestD = Infinity;
        manual.forEach((r, i) => { const [la, ln] = r[0]; const dd = (la - vehiclePos.lat) ** 2 + (ln - vehiclePos.lng) ** 2; if (dd < bestD) { bestD = dd; hl = i; } });
      }
      manual.forEach((r, i) => {
        const isHl = hl === i;
        const c = isHl ? "#16a34a" : (hl != null ? "#94a3b8" : ROUTE_COLORS[i % 2]);
        const weight = isHl ? 6 : (hl != null ? 3 : 4);
        const opacity = (hl != null && !isHl) ? 0.5 : 0.85;
        if (r.length >= 2) L.polyline(r, { color: c, weight, opacity }).addTo(grp);
        L.marker(r[0], { icon: numIcon(i + 1, c) }).addTo(grp).bindPopup(isHl ? `Aanrijroute ${i + 1} — dichtstbij` : `Aanrijroute ${i + 1}`);
        r.forEach((p) => pts.push(p));
      });
    } else if (tweezijdig && (!cfg || cfg.auto_approach_lines !== false)) {
      const d = 0.0035;
      [[lat + d, lng], [lat - d, lng]].forEach((p, i) => {
        const isHl = assignedRoute === i;
        const c = isHl ? "#16a34a" : (assignedRoute != null ? "#94a3b8" : ROUTE_COLORS[i % 2]);
        L.polyline([p, [lat, lng]], { color: c, weight: isHl ? 5 : 3, opacity: isHl ? 0.9 : 0.7, dashArray: "6 6" }).addTo(grp);
        L.marker(p, { icon: numIcon(i + 1, c) }).addTo(grp).bindPopup(isHl ? `Aanrijzijde ${i + 1} — jouw zijde` : `Aanrijzijde ${i + 1} (indicatief)`);
        pts.push(p);
      });
    }
    if (vehiclePos) { L.circleMarker([vehiclePos.lat, vehiclePos.lng], { radius: 6, color: "#16a34a", fillColor: "#22c55e", fillOpacity: 1, weight: 2 }).addTo(grp).bindPopup("Voertuig"); pts.push([vehiclePos.lat, vehiclePos.lng]); }
    if (pts.length) { pts.push([lat, lng]); try { map.fitBounds(L.latLngBounds(pts).pad(0.3)); } catch (_) {} }
  }, [routes, tweezijdig, lat, lng, cfg, vehiclePos, assignedRoute]);

  // Read-only overlay of meldkamer drawings (lines/markers/polygons) for this incident.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (drawRef.current) { map.removeLayer(drawRef.current); drawRef.current = null; }
    if (drawings && Array.isArray(drawings.features) && drawings.features.length) {
      drawRef.current = L.geoJSON(drawings, {
        pointToLayer: (f, ll) => L.marker(ll, { icon: pin((f.properties && f.properties.color) || "#7c3aed") }),
        style: (f) => { const col = (f.properties && f.properties.color) || (f.geometry.type.indexOf("Polygon") >= 0 ? "#dc2626" : "#2563eb"); return f.geometry.type.indexOf("Polygon") >= 0 ? { color: col, weight: 2, fillColor: col, fillOpacity: 0.12 } : { color: col, weight: 4, opacity: 0.85 }; },
        onEachFeature: (f, layer) => { const t = f.properties && f.properties.label; if (t) layer.bindPopup(String(t)); },
      }).addTo(map);
    }
  }, [drawings]);

  // Read-only overlay of the shared meldkamer map layer (independent of incidents), with a toggle.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (sharedRef.current) { map.removeLayer(sharedRef.current); sharedRef.current = null; }
    if (showShared && sharedLayer && Array.isArray(sharedLayer.features) && sharedLayer.features.length) {
      sharedRef.current = L.geoJSON(sharedLayer, {
        pointToLayer: (f, ll) => L.marker(ll, { icon: pin((f.properties && f.properties.color) || "#0d9488") }),
        style: (f) => { const col = (f.properties && f.properties.color) || "#0d9488"; return f.geometry.type.indexOf("Polygon") >= 0 ? { color: col, weight: 2, fillColor: col, fillOpacity: 0.1, dashArray: "4 4" } : { color: col, weight: 3, opacity: 0.8, dashArray: "4 4" }; },
        onEachFeature: (f, layer) => { const t = f.properties && f.properties.label; if (t) layer.bindPopup(String(t)); },
      }).addTo(map);
    }
  }, [sharedLayer, showShared]);

  const usedColors = new Set();
  if (drawings && Array.isArray(drawings.features)) drawings.features.forEach((f) => usedColors.add((f.properties && f.properties.color) || (f.geometry.type.indexOf("Polygon") >= 0 ? "#dc2626" : "#2563eb")));
  if (showShared && sharedLayer && Array.isArray(sharedLayer.features)) sharedLayer.features.forEach((f) => usedColors.add((f.properties && f.properties.color) || "#0d9488"));
  const legendEntries = legend ? Object.entries(legend).filter(([c, v]) => v && usedColors.has(c)) : [];
  if (lat == null || lng == null) return null;
  return (
    <MapShell heightClass="h-40" testid="incident-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
      <button data-testid="incident-map-satellite" onClick={() => setSat((s) => !s)}
        className={`absolute left-2 top-2 z-[1001] rounded-lg border px-2.5 py-1 text-[11px] font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
        {sat ? "Satelliet aan" : "Satelliet"}
      </button>
      {sharedLayer && Array.isArray(sharedLayer.features) && sharedLayer.features.length > 0 && (
        <button data-testid="incident-map-sharedlayer" onClick={() => setShowShared((s) => !s)}
          className={`absolute left-2 top-11 z-[1001] rounded-lg border px-2.5 py-1 text-[11px] font-semibold shadow ${showShared ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
          Tekenlaag
        </button>
      )}
      <div ref={el} data-testid="incident-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
      {legendEntries.length > 0 && (
        <div data-testid="map-legend" className="absolute bottom-2 left-2 z-[1001] max-w-[48%] rounded-lg border border-slate-200 bg-white/95 px-2 py-1.5 shadow">
          <div className="mb-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-400">Legenda</div>
          <div className="space-y-0.5">
            {legendEntries.map(([c, v]) => (
              <div key={c} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-white shadow" style={{ backgroundColor: c }} />
                <span className="truncate text-[10px] font-medium text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </MapShell>
  );
}

// Interactive picker: click the map to drop/move a pin. Calls onPick({lat,lng}).
export function LocationPicker({ value, onPick, buildings = [] }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const center = value ? [value.lat, value.lng] : (buildings[0] ? [buildings[0].lat, buildings[0].lng] : [52.11, 5.18]);
    const map = L.map(el.current, { attributionControl: false }).setView(center, 15);
    mapRef.current = map;
    loadMapConfig().then((cfg) => { const b = makeBaseLayer(cfg, false).addTo(map); b.bringToBack(); });
    buildings.forEach((b) => {
      L.marker([b.lat, b.lng], { icon: pin("#1e40af") }).addTo(map).bindPopup(b.name);
    });
    if (value) markerRef.current = L.marker([value.lat, value.lng], { icon: pin() }).addTo(map);
    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
      else markerRef.current = L.marker([lat, lng], { icon: pin() }).addTo(map);
      onPick({ lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) });
    });
    setTimeout(() => map.invalidateSize(), 200);
  }, []); // eslint-disable-line
  return (
    <MapShell heightClass="h-56" testid="location-picker" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
      <div ref={el} data-testid="location-picker" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
    </MapShell>
  );
}

// Editor: click the map to add points to one of TWO approach routes. Value = [[[lat,lng],...],[...]].
export function ApproachRouteEditor({ center, value = [], onChange }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0); activeRef.current = active;
  const routes = value && value.length ? value : [[], []];
  const routesRef = useRef(routes); routesRef.current = routes;

  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const c = center && center.lat != null ? [center.lat, center.lng] : [52.11, 5.18];
    const map = L.map(el.current, { attributionControl: false }).setView(c, 16);
    mapRef.current = map;
    loadMapConfig().then((cfg) => { const b = makeBaseLayer(cfg, false).addTo(map); b.bringToBack(); });
    L.marker(c, { icon: pin("#dc2626") }).addTo(map).bindPopup("Gebouw");
    map.on("click", (e) => {
      const p = [Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))];
      const cur = (routesRef.current || [[], []]).map((r) => [...(r || [])]);
      while (cur.length < 2) cur.push([]);
      cur[activeRef.current] = [...cur[activeRef.current], p];
      onChange(cur);
    });
    setTimeout(() => map.invalidateSize(), 200);
  }, []); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (layerRef.current) map.removeLayer(layerRef.current);
    const grp = L.layerGroup().addTo(map); layerRef.current = grp;
    (routes || []).forEach((r, i) => {
      if (!r || !r.length) return;
      const c = ROUTE_COLORS[i % 2];
      if (r.length >= 2) L.polyline(r, { color: c, weight: 4, opacity: 0.85 }).addTo(grp);
      r.forEach((pt) => L.circleMarker(pt, { radius: 4, color: c, fillColor: c, fillOpacity: 1 }).addTo(grp));
      L.marker(r[0], { icon: numIcon(i + 1, c) }).addTo(grp);
    });
  }, [value]); // eslint-disable-line

  const mutate = (fn) => { const cur = (routesRef.current || [[], []]).map((r) => [...(r || [])]); while (cur.length < 2) cur.push([]); fn(cur); onChange(cur); };
  const undo = (i) => mutate((cur) => { cur[i] = cur[i].slice(0, -1); });
  const clear = (i) => mutate((cur) => { cur[i] = []; });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {[0, 1].map((i) => (
          <button key={i} type="button" data-testid={`route-select-${i}`} onClick={() => setActive(i)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${active === i ? "text-white" : "border border-slate-200 bg-white text-slate-600"}`}
            style={active === i ? { backgroundColor: ROUTE_COLORS[i] } : {}}>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ROUTE_COLORS[i] }} /> Route {i + 1} ({(routes[i] || []).length})
          </button>
        ))}
        <button type="button" data-testid={`route-undo-${active}`} onClick={() => undo(active)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Punt terug</button>
        <button type="button" data-testid={`route-clear-${active}`} onClick={() => clear(active)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">Route wissen</button>
      </div>
      <MapShell heightClass="h-56" testid="route-editor" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
        <div ref={el} data-testid="route-editor" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
      </MapShell>
      <p className="text-[11px] text-slate-400">Kies Route 1 of 2 en klik op de kaart om aanrijpunten toe te voegen. Beide routes eindigen bij het gebouw.</p>
    </div>
  );
}

// Single-point picker: click the map to set a coordinate. value={lat,lng} | null.
export function PointPicker({ value, onChange }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const mkRef = useRef(null);
  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const c = value && value.lat != null ? [value.lat, value.lng] : [52.11, 5.18];
    const map = L.map(el.current, { attributionControl: false }).setView(c, value && value.lat != null ? 15 : 8);
    mapRef.current = map;
    loadMapConfig().then((cfg) => {
      const b = makeBaseLayer(cfg, false).addTo(map); b.bringToBack();
      if ((!value || value.lat == null) && cfg && cfg.mode === "static" && cfg.bounds) {
        try { map.fitBounds(cfg.bounds); } catch (e) {}
      }
    });
    if (value && value.lat != null) mkRef.current = L.marker(c, { icon: pin("#dc2626") }).addTo(map);
    map.on("click", (e) => {
      const p = { lat: Number(e.latlng.lat.toFixed(6)), lng: Number(e.latlng.lng.toFixed(6)) };
      if (mkRef.current) mkRef.current.setLatLng([p.lat, p.lng]); else mkRef.current = L.marker([p.lat, p.lng], { icon: pin("#dc2626") }).addTo(map);
      onChange(p);
    });
    setTimeout(() => map.invalidateSize(), 200);
  }, []); // eslint-disable-line
  return (
    <MapShell heightClass="h-52" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
      <div ref={el} data-testid="point-picker" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
    </MapShell>
  );
}

// Editor with leaflet-draw: lines, markers and free polygons for one incident. Saves a GeoJSON FeatureCollection.
export function IncidentDrawEditor({ lat, lng, label, initial, onSave, onCancel, saving }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const fgRef = useRef(null);
  const baseRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [sat, setSat] = useState(false);
  const [activeColor, setActiveColor] = useState("#2563eb");
  const activeColorRef = useRef("#2563eb");
  activeColorRef.current = activeColor;
  const [clickMode, setClickMode] = useState("label");
  const clickModeRef = useRef("label");
  clickModeRef.current = clickMode;
  const drawCtlRef = useRef(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    if (!el.current || mapRef.current || lat == null || lng == null) return;
    const map = L.map(el.current, { attributionControl: false }).setView([lat, lng], 16);
    mapRef.current = map;
    loadMapConfig().then((c) => setCfg(c || {}));
    L.marker([lat, lng], { icon: pin() }).addTo(map).bindPopup(label || "");
    const fg = new L.FeatureGroup().addTo(map); fgRef.current = fg;
    let saveTimer;
    const scheduleSave = () => { clearTimeout(saveTimer); saveTimer = setTimeout(() => { if (fgRef.current) onSaveRef.current(fgRef.current.toGeoJSON()); }, 700); };
    const applyLabel = (layer, lb) => {
      layer.feature = layer.feature || { type: "Feature", properties: {} };
      layer.feature.properties = layer.feature.properties || {};
      layer.feature.properties.label = lb || "";
      layer.unbindTooltip();
      if (lb) layer.bindTooltip(lb, { permanent: true, direction: "top", className: "draw-label" });
      scheduleSave();
    };
    const editLabel = async (layer) => { const nl = await promptDialog({ title: "Label", message: "Label voor deze tekening (bijv. 'Opstelplaats', 'Aanval Zijde A'). Leeg = geen label.", defaultValue: (layer.feature && layer.feature.properties && layer.feature.properties.label) || "", confirmText: "Opslaan" }); if (nl != null) applyLabel(layer, nl.trim()); };
    const applyColor = (layer, col) => {
      if (layer.setStyle) layer.setStyle({ color: col, fillColor: col });
      if (layer.setIcon) layer.setIcon(pin(col));
      layer.feature = layer.feature || { type: "Feature", properties: {} };
      layer.feature.properties = layer.feature.properties || {};
      layer.feature.properties.color = col;
      scheduleSave();
    };
    const onShapeClick = (layer) => { if (clickModeRef.current === "color") applyColor(layer, activeColorRef.current); else editLabel(layer); };
    if (initial && Array.isArray(initial.features)) {
      L.geoJSON(initial, {
        pointToLayer: (f, ll) => L.marker(ll, { icon: pin((f.properties && f.properties.color) || "#7c3aed") }),
        style: (f) => { const col = (f.properties && f.properties.color) || (f.geometry.type.indexOf("Polygon") >= 0 ? "#dc2626" : "#2563eb"); return f.geometry.type.indexOf("Polygon") >= 0 ? { color: col, weight: 2, fillColor: col, fillOpacity: 0.12 } : { color: col, weight: 4 }; },
      }).eachLayer((l) => { fg.addLayer(l); const lb = l.feature && l.feature.properties && l.feature.properties.label; if (lb) l.bindTooltip(lb, { permanent: true, direction: "top", className: "draw-label" }); l.on("click", () => onShapeClick(l)); });
    }
    const drawCtl = new L.Control.Draw({
      position: "topright",
      edit: { featureGroup: fg, remove: true },
      draw: {
        polyline: { shapeOptions: { color: activeColorRef.current, weight: 4 } },
        polygon: { allowIntersection: false, showArea: false, shapeOptions: { color: activeColorRef.current, weight: 2, fillOpacity: 0.12 } },
        marker: { icon: pin(activeColorRef.current) },
        rectangle: false, circle: false, circlemarker: false,
      },
    });
    drawCtlRef.current = drawCtl;
    map.addControl(drawCtl);
    map.on(L.Draw.Event.CREATED, async (e) => {
      const col = activeColorRef.current;
      if (e.layer.setStyle) e.layer.setStyle({ color: col, fillColor: col });
      if (e.layer.setIcon) e.layer.setIcon(pin(col));
      e.layer.feature = e.layer.feature || { type: "Feature", properties: {} };
      e.layer.feature.properties = e.layer.feature.properties || {};
      e.layer.feature.properties.color = col;
      fg.addLayer(e.layer);
      e.layer.on("click", () => onShapeClick(e.layer));
      await editLabel(e.layer);
      scheduleSave();
    });
    map.on(L.Draw.Event.EDITED, scheduleSave);
    map.on(L.Draw.Event.DELETED, scheduleSave);
    setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(saveTimer);
  }, []); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(cfg, sat).addTo(map);
    baseRef.current.bringToBack();
  }, [cfg, sat]);

  useEffect(() => {
    const c = drawCtlRef.current; if (!c || !c.setDrawingOptions) return;
    c.setDrawingOptions({ polyline: { shapeOptions: { color: activeColor, weight: 4 } }, polygon: { shapeOptions: { color: activeColor, weight: 2, fillOpacity: 0.12 } }, marker: { icon: pin(activeColor) } });
  }, [activeColor]);

  const clearAll = () => { if (fgRef.current) { fgRef.current.clearLayers(); onSave({ type: "FeatureCollection", features: [] }); } };

  if (lat == null || lng == null) return <div className="text-xs text-slate-400">Geen locatie om op te tekenen.</div>;
  return (
    <MapShell heightClass="h-72" testid="draw-editor" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
      <div ref={el} data-testid="draw-editor" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
      <div className="absolute left-2 top-2 z-[1001] flex max-w-[calc(100%-4.5rem)] flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white/95 px-2 py-1.5 shadow" data-testid="draw-colors">
        <button type="button" data-testid="draw-map-satellite" onClick={() => setSat((s) => !s)}
          className={`rounded px-2 py-0.5 text-[11px] font-semibold ${sat ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{sat ? "Satelliet aan" : "Satelliet"}</button>
        <span className="mx-0.5 h-4 w-px bg-slate-200" />
        {PALETTE.map((p) => (
          <button key={p.c} type="button" data-testid={`draw-color-${p.c}`} title={p.label} onClick={() => setActiveColor(p.c)}
            className={`h-6 w-6 rounded-full border-2 border-white shadow transition ${activeColor === p.c ? "ring-2 ring-offset-1 ring-slate-500" : ""}`}
            style={{ backgroundColor: p.c }} />
        ))}
        <span className="mx-0.5 h-4 w-px bg-slate-200" />
        <div className="inline-flex overflow-hidden rounded-lg border border-slate-200">
          <button type="button" data-testid="draw-clickmode-label" onClick={() => setClickMode("label")} className={`px-2 py-1 text-[11px] font-semibold ${clickMode === "label" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Label</button>
          <button type="button" data-testid="draw-clickmode-color" onClick={() => setClickMode("color")} className={`px-2 py-1 text-[11px] font-semibold ${clickMode === "color" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Kleur</button>
        </div>
      </div>
      <div className="absolute left-2 bottom-2 z-[1001] flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-2 py-1.5 shadow">
        <span data-testid="draw-autosave-status" className={`inline-flex items-center gap-1 text-[11px] font-semibold ${saving ? "text-amber-600" : "text-emerald-600"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${saving ? "bg-amber-500" : "bg-emerald-500"}`} />{saving ? "Opslaan…" : "Automatisch opgeslagen"}
        </span>
        <button type="button" data-testid="draw-clear" onClick={clearAll} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50">Wissen</button>
        <button type="button" data-testid="draw-cancel" onClick={onCancel} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">Sluiten</button>
      </div>
    </MapShell>
  );
}
